import React, { useState, useRef } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';

const XCounterPage = () => {
  const [text, setText] = useState('');
  const [charToCount, setCharToCount] = useState('');
  const [countResult, setCountResult] = useState(null);
  const [presetCounts, setPresetCounts] = useState({});
  const [error, setError] = useState('');
  
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    
    // Update preset counts
    updatePresetCounts(newText);
    
    // If user has typed a character to count, update that count too
    if (charToCount) {
      countCharacter(newText, charToCount);
    }
  };
  
  const handleCharToCountChange = (e) => {
    const char = e.target.value;
    setCharToCount(char);
    
    if (char) {
      countCharacter(text, char);
    } else {
      setCountResult(null);
      setError('');
    }
  };
  
  const countCharacter = (textToSearch, char) => {
    if (!char) {
      setCountResult(null);
      setError('Please enter a character to count.');
      return;
    }
    
    try {
      let count;
      let searchTerm = char;
      
      // Check if it's an escape sequence
      if (char.startsWith('\\')) {
        if (char === '\\n') searchTerm = '\n';
        else if (char === '\\t') searchTerm = '\t';
        else if (char === '\\s') searchTerm = ' ';
        else if (char === '\\r') searchTerm = '\r';
        else searchTerm = char.slice(1);
      }
      
      // Try to create a regex if the char is a valid pattern
      if (char.length > 1 && !char.startsWith('\\')) {
        try {
          const regex = new RegExp(char, 'g');
          count = (textToSearch.match(regex) || []).length;
        } catch (e) {
          // If regex creation fails, count as literal string
          count = textToSearch.split(searchTerm).length - 1;
        }
      } else {
        count = textToSearch.split(searchTerm).length - 1;
      }
      
      setCountResult({
        character: char,
        count: count
      });
      setError('');
    } catch (e) {
      setError('Error counting character: ' + e.message);
    }
  };
  
  const updatePresetCounts = (newText) => {
    const presets = {
      'Space': (newText.match(/ /g) || []).length,
      'Comma': (newText.match(/,/g) || []).length,
      'Period': (newText.match(/\./g) || []).length,
      'Semicolon': (newText.match(/;/g) || []).length,
      'Colon': (newText.match(/:/g) || []).length,
      'Tab': (newText.match(/\t/g) || []).length,
      'Newline': (newText.match(/\n/g) || []).length
    };
    
    setPresetCounts(presets);
  };

  const handleScroll = () => {
    if (lineNumbersRef.current && textareaRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const clearAll = () => {
    setText('');
    setCharToCount('');
    setCountResult(null);
    setPresetCounts({
      'Space': 0,
      'Comma': 0,
      'Period': 0,
      'Semicolon': 0,
      'Colon': 0,
      'Tab': 0,
      'Newline': 0
    });
    setError('');
  };
  
  const handlePresetClick = (char) => {
    let charSymbol = '';
    
    switch(char) {
      case 'Space': charSymbol = ' '; break;
      case 'Comma': charSymbol = ','; break;
      case 'Period': charSymbol = '.'; break;
      case 'Semicolon': charSymbol = ';'; break;
      case 'Colon': charSymbol = ':'; break;
      case 'Tab': charSymbol = '\\t'; break;
      case 'Newline': charSymbol = '\\n'; break;
      default: charSymbol = char;
    }
    
    setCharToCount(charSymbol);
    countCharacter(text, charSymbol);
  };

  return (
    <div className="container" style={{ width: '90%', maxWidth: '1400px' }}>
      <SEO
        title="X Counter - Count Any Character | YourJSONTools"
        description="Count occurrences of any character, symbol or pattern in your text. Count spaces, commas, or custom patterns."
        keywords="character counter, comma counter, space counter, text analysis, pattern counter"
      />

      <h1>X Counter</h1>
      <p className="tool-description">
        Count the number of occurrences of any character, symbol, or pattern in your text.
        You can count spaces, commas, periods, or custom characters.
      </p>

      <div className="x-counter-container">
        <div className="tool-section">
          <div className="editor-header">
            <h3>Enter Text</h3>
            <div className="toolbar">
              <button onClick={clearAll} className="toolbar-button clear-button">Clear</button>
            </div>
          </div>
          
          <div className="editor-wrapper">
            <TextareaWithLines
              value={text}
              onChange={handleTextChange}
              onScroll={handleScroll}
              textareaRef={textareaRef}
              lineNumbersRef={lineNumbersRef}
              placeholder="Type or paste your text here..."
            />
          </div>
        </div>

        <div className="counter-section">
          <div className="counter-input">
            <h3>Character to Count</h3>
            <div className="counter-input-row">
              <input
                type="text"
                value={charToCount}
                onChange={handleCharToCountChange}
                placeholder="Enter character(s) to count..."
                className="character-input"
              />
            </div>
            
            <div className="preset-buttons">
              <h4>Quick Count</h4>
              <div className="preset-grid">
                {Object.keys(presetCounts).map(char => (
                  <button 
                    key={char}
                    onClick={() => handlePresetClick(char)}
                    className={`preset-button ${charToCount === (char === 'Tab' ? '\\t' : char === 'Newline' ? '\\n' : char === 'Space' ? ' ' : char.toLowerCase()) ? 'active' : ''}`}
                  >
                    {char}: {presetCounts[char] || 0}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {countResult && (
            <div className="count-result">
              <h3>Count Result</h3>
              <div className="result-display">
                <div className="result-character">
                  "{countResult.character === ' ' ? 'Space' : 
                    countResult.character === '\t' ? 'Tab' : 
                    countResult.character === '\n' ? 'Newline' : 
                    countResult.character}"
                </div>
                <div className="result-count">{countResult.count}</div>
                <div className="result-label">occurrences</div>
              </div>
            </div>
          )}
          
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>

      <style>{`
        .x-counter-container {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }
        
        @media (min-width: 1024px) {
          .x-counter-container {
            grid-template-columns: 1fr 1fr;
          }
        }
        
        .tool-section {
          display: flex;
          flex-direction: column;
          background: #f5f5f5;
          border-radius: 6px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          background: #eaeaea;
          border-bottom: 1px solid #ddd;
        }
        
        .editor-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #333;
        }
        
        .toolbar {
          display: flex;
          gap: 8px;
        }
        
        .toolbar-button {
          padding: 0.4rem 0.8rem;
          font-size: 0.875rem;
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .toolbar-button:hover {
          background: #f0f0f0;
        }
        
        .clear-button {
          background: #607d8b;
          color: white;
          border-color: #607d8b;
        }
        
        .clear-button:hover {
          background: #455a64;
        }
        
        .editor-wrapper {
          flex: 1;
          min-height: 700px;
          position: relative;
        }
        
        .counter-section {
          background: #f5f5f5;
          border-radius: 6px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
        }
        
        .counter-section h3 {
          margin-top: 0;
          margin-bottom: 1rem;
          font-size: 1.1rem;
          font-weight: 600;
          color: #333;
        }
        
        .counter-input-row {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .character-input {
          flex: 1;
          padding: 0.75rem;
          font-size: 1rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          transition: border-color 0.2s;
        }
        
        .character-input:focus {
          outline: none;
          border-color: #2196f3;
        }
        
        .preset-buttons {
          margin-top: 1.5rem;
        }
        
        .preset-buttons h4 {
          margin-top: 0;
          margin-bottom: 0.75rem;
          font-size: 1rem;
          font-weight: 500;
          color: #333;
        }
        
        .preset-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 0.75rem;
        }
        
        .preset-button {
          padding: 0.6rem 0.5rem;
          background: white;
          border: 1px solid #ddd;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.9rem;
          color: #666;
        }
        
        .preset-button:hover {
          background: #f0f0f0;
          transform: translateY(-2px);
          box-shadow: 0 3px 5px rgba(0,0,0,0.1);
        }
        
        .preset-button.active {
          background: #e3f2fd;
          border-color: #2196f3;
          color: #0d47a1;
          font-weight: 500;
        }
        
        /* Colorful preset buttons */
        .preset-grid button:nth-child(1) {
          background: #e3f2fd;
          border-color: #90caf9;
          color: #1565c0;
        }
        
        .preset-grid button:nth-child(2) {
          background: #e8f5e9;
          border-color: #a5d6a7;
          color: #2e7d32;
        }
        
        .preset-grid button:nth-child(3) {
          background: #fff3e0;
          border-color: #ffcc80;
          color: #ef6c00;
        }
        
        .preset-grid button:nth-child(4) {
          background: #f3e5f5;
          border-color: #ce93d8;
          color: #7b1fa2;
        }
        
        .preset-grid button:nth-child(5) {
          background: #e0f7fa;
          border-color: #80deea;
          color: #00838f;
        }
        
        .preset-grid button:nth-child(6) {
          background: #fbe9e7;
          border-color: #ffab91;
          color: #d84315;
        }
        
        .preset-grid button:nth-child(7) {
          background: #f1f8e9;
          border-color: #c5e1a5;
          color: #558b2f;
        }
        
        .preset-grid button:hover {
          filter: brightness(0.95);
          transform: translateY(-2px);
          box-shadow: 0 3px 5px rgba(0,0,0,0.1);
        }
        
        .count-result {
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid #ddd;
        }
        
        .result-display {
          display: flex;
          flex-direction: column;
          align-items: center;
          background: white;
          padding: 1.5rem;
          border-radius: 6px;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
          margin-top: 1rem;
        }
        
        .result-character {
          font-size: 1.2rem;
          margin-bottom: 0.5rem;
          color: #555;
        }
        
        .result-count {
          font-size: 3rem;
          font-weight: bold;
          color: #2196f3;
        }
        
        .result-label {
          font-size: 1rem;
          color: #666;
          margin-top: 0.25rem;
        }
        
        .error-message {
          color: #d32f2f;
          margin-top: 1rem;
          padding: 0.75rem;
          background: #ffebee;
          border-radius: 4px;
          border-left: 4px solid #d32f2f;
        }
      `}</style>

      <AdUnit />
    </div>
  );
};

export default XCounterPage; 